import {
  type GoogleDriveItem,
  googleDriveDownloadDocument,
  resolveSharepointUrl,
  type SharePointDocumentMeta,
  type SharePointItem,
  sharepointDownloadDocument,
} from "@/api";

/** A cloud file's extracted content, ready to embed in a message (the backend never stores it). */
export interface CloudDoc {
  meta: SharePointDocumentMeta;
  /** Markdown section for the AI context ("### name" + text). */
  section: string;
  /** Presentations come back as slide images instead of text. */
  slideImages: string[];
  /** Context for the model alongside the file (e.g. the subfolders of the folder it came from). */
  note?: string;
}

interface Extracted {
  filename: string;
  source_url?: string;
  size: number;
  mime_type: string;
  content: string;
  truncated?: boolean;
  slide_images?: string[] | null;
}

// Same section format frontend/ sends (hooks/useChatAttachments.ts), so the model sees identical context.
function toDoc(doc: Extracted, fallbackUrl: string): CloudDoc {
  const meta = { filename: doc.filename, url: doc.source_url || fallbackUrl, size: doc.size, mimeType: doc.mime_type };
  const slides = doc.slide_images ?? [];
  if (slides.length > 0) {
    return {
      meta,
      section: `### ${doc.filename}\n*(Presentation with ${slides.length} slides — images attached)*`,
      slideImages: slides,
    };
  }
  const truncated = doc.truncated ? `*(Document truncated — original size: ${doc.size} bytes)*\n` : "";
  return { meta, section: `### ${doc.filename}\n${truncated}\n${doc.content}`, slideImages: [] };
}

export async function loadSharePointDoc(item: SharePointItem): Promise<CloudDoc> {
  const { data } = await sharepointDownloadDocument({ query: { url: item.web_url }, throwOnError: true });
  return toDoc(data, item.web_url);
}

/** SharePoint / OneDrive document links (as frontend/ detects them). */
const SHAREPOINT_URL = /https?:\/\/[a-zA-Z0-9-]+(?:-my)?\.sharepoint\.com\/[^\s)>"]+/g;

/**
 * Pulls SharePoint links that `next` added compared to `previous` (typed or
 * pasted) out of the text, so they can become attachments instead.
 */
export function takeNewSharePointLinks(previous: string, next: string): { text: string; urls: string[] } {
  const finished = (text: string, url: string) => /\s/.test(text.charAt(text.indexOf(url) + url.length));
  // Links already complete before this change were left there on purpose.
  const before = new Set((previous.match(SHAREPOINT_URL) ?? []).filter((url) => finished(previous, url)));
  // Pasted, or finished typing (followed by whitespace): never grab a link mid-typing.
  const pasted = next.length - previous.length > 1;
  const urls = [...new Set(next.match(SHAREPOINT_URL) ?? [])].filter(
    (url) => !before.has(url) && (pasted || finished(next, url)),
  );
  if (urls.length === 0) return { text: next, urls };
  let text = next;
  for (const url of urls) text = text.split(url).join("");
  return { text: text.replace(/[ \t]{2,}/g, " ").trim(), urls };
}

/** A readable name for a link until it resolves (last path segment, or the host). */
export function linkName(url: string): string {
  try {
    const { pathname, host } = new URL(url);
    return decodeURIComponent(pathname.split("/").findLast(Boolean) ?? host);
  } catch {
    return url;
  }
}

/** A pasted link that turned out to be a folder: the composer offers its files instead of failing. */
export class SharePointFolderLink extends Error {
  constructor(readonly folder: SharePointItem) {
    super(folder.name);
    this.name = "SharePointFolderLink";
  }
}

export async function loadSharePointLink(url: string): Promise<CloudDoc> {
  const { data } = await resolveSharepointUrl({ query: { url }, throwOnError: true });
  if (data.is_folder) throw new SharePointFolderLink({ ...data, web_url: data.web_url || url });
  return loadSharePointDoc(data);
}

/**
 * Tells the model which subfolders of a pasted folder weren't attached, with the IDs its
 * `list_sharepoint_folder` tool needs, so it can look inside one when asked. Model context: not translated.
 */
export function subfolderNote(folder: SharePointItem, subfolders: SharePointItem[]): string | undefined {
  if (subfolders.length === 0) return undefined;
  const list = subfolders.map((sub) => `- ${sub.name} (folder_id: ${sub.id})`).join("\n");
  return [
    `### Folder: ${folder.name}`,
    `The user attached files from the SharePoint folder "${folder.name}" (${folder.web_url}). ` +
      "It also has these subfolders, which were not attached:",
    list,
    `If the user asks about them, list one with list_sharepoint_folder (drive_id: ${folder.drive_id}, folder_id above).`,
  ].join("\n");
}

export async function loadDriveDoc(item: GoogleDriveItem): Promise<CloudDoc> {
  const { data } = await googleDriveDownloadDocument({ path: { file_id: item.id }, throwOnError: true });
  return toDoc(data, item.web_url ?? "");
}

/** The message fields for a set of cloud docs (`sharepoint*` names are historical; Drive uses them too). */
export function cloudMessageFields(docs: CloudDoc[]) {
  if (docs.length === 0) return {};
  const slides = docs.flatMap((d) => d.slideImages);
  const notes = [...new Set(docs.flatMap((d) => (d.note ? [d.note] : [])))];
  return {
    sharepointDocuments: docs.map((d) => d.meta),
    sharepointContent: `## Attached Documents\n\n${[...docs.map((d) => d.section), ...notes].join("\n\n---\n\n")}`,
    sharepointSlideImages: slides.length > 0 ? slides : undefined,
  };
}
